import { Button, ButtonGroup } from "@components/button";
import { IDayAction } from "@components/calendar/Calendar.utils";
import { getMonthDays, isSameDay } from "@components/inputs/date/utils";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { createPath } from "@odata/BindingContext";
import {
    EntitySetName,
    IPrAttendanceEntity,
    IPrPayrollSettingEntity,
    IPrPayslipEntity,
    PrPayslipEmploymentEntity,
    PrPayslipEntity
} from "@odata/GeneratedEntityTypes";
import { PrAttendanceStatusCode } from "@odata/GeneratedEnums";
import { viewSwitchPath } from "@pages/payroll/attendance/Attendance.def";
import { IntervalType } from "@pages/payroll/attendance/Attendance.types";
import { logger } from "@utils/log";
import { cloneDeep } from "lodash";
import React, { ReactElement } from "react";

import { AppContext } from "../../../contexts/appContext/AppContext.types";
import { isCustomerSession } from "../../../contexts/appContext/AppContext.utils";
import { withPermissionContext } from "../../../contexts/permissionContext/withPermissionContext";
import DateType, { DateFormat, getUtcDayjs } from "../../../types/Date";
import { IFormStorageDefaultCustomData } from "../../../views/formView/FormStorage";
import { FormViewForExtend, IFormViewProps } from "../../../views/formView/FormView";
import {
    clearAttendance,
    fetchPayrollSettingsForAttendance,
    getDayFund,
    getDayIntervalsMinutes,
    getDaySaldo,
    getMonthHolidays,
    pairIntervalsWithExistingIds,
    parseDayActionsFromData,
    refreshBalance,
    transformWorkIntervalsBeforeSave
} from "./Attendance.utils";

export interface IAttendanceCustomData extends IFormStorageDefaultCustomData {
    selectedDays: Set<number>;
    focusedDay: number;
    saldoMap: Record<number, number>;
    workedHoursMap: Record<number, number>;
    actions?: Record<number, IDayAction[]>;
    holidays?: Date[];
    // overtime work carried over in minutes
    lastTimeOvertimeWork?: number;
    payrollSettings?: IPrPayrollSettingEntity;
}

class AttendanceFormView extends FormViewForExtend<IPrAttendanceEntity, IFormViewProps<IPrAttendanceEntity, IAttendanceCustomData>> {
    static contextType = AppContext;
    static defaultProps = {
        title: "Attendance:Title",
        formProps: {
            shouldHideAuditTrail: true
        }
    };

    get hideBreadcrumbsBack(): boolean {
        return true;
    }

    shouldHideButtons(): boolean {
        // buttons should be visible even on customer portal
        //  todo: consider permissions...
        return false;
    }

    // Customer session is limited to only maintain attendance without possibility to lock/Unlock it.
    get canLockAttendance(): boolean {
        return !isCustomerSession(this.context);
    }

    async getLastMonthOvertime(): Promise<number> {
        const employeeId = this.entity.Employment?.Employee?.Id;
        if (!employeeId) {
            return 0;
        }
        let lastMonth = this.entity.Month - 1;
        let lastMonthYear = this.entity.Year;
        if (this.entity.Month === 1) {
            lastMonth = 12;
            lastMonthYear = this.entity.Year - 1;
        }

        try {
            const res = await this.props.storage.oData.getEntitySetWrapper(EntitySetName.PrPayslips).query()
                .filter(`${createPath(PrPayslipEntity.Employee, "Id")} eq ${employeeId} 
                                and ${PrPayslipEntity.Month} eq ${lastMonth} 
                                and ${PrPayslipEntity.Year} eq ${lastMonthYear}`)
                .expand(PrPayslipEntity.Employments, q => {
                    return q.expand(PrPayslipEmploymentEntity.Employment, q2 => q2.select("Id"))
                        .select(PrPayslipEmploymentEntity.OvertimeCarriedOver);
                })
                .fetchData<IPrPayslipEntity[]>();

            return res.value[0]?.Employments.find(e => e.Employment.Id === this.entity.Employment.Id)?.OvertimeCarriedOver ?? 0;
        } catch (e) {
            logger.error("Failed to fetch last month overtime", e);
        }
        return 0;
    }

    get payslipDayjs() {
        // return first day of the month - note, do not use actual day,
        // as it may behave differently according to current day in month, e.g. 31.1. -> February
        return getUtcDayjs().set("date", 1).set("month", this.entity.Month - 1).set("year", this.entity.Year);
    }

    getFormSubtitle = (): string => {
        return `/${DateType.format(this.payslipDayjs, DateFormat.monthAndYear)}/`;
    };

    async onAfterLoad() {
        const storage = this.props.storage;
        storage.setDefaultValueByPath(viewSwitchPath);

        const monthDate = this.payslipDayjs;

        const lastTimeOvertimeWork = await this.getLastMonthOvertime();
        const payrollSettings = await fetchPayrollSettingsForAttendance(this.props.storage);

        const holidays = await getMonthHolidays(monthDate, this.entity.Year, this.context.getCompany().LegalAddress.CountryCode);
        const monthDays = getMonthDays(monthDate);

        storage.setCustomData({ holidays }); // must be before saldo map, as it uses holidays to calculate day fund
        const saldoMap = monthDays.reduce((map: Record<number, number>, day) => {
            const dayIndex = day.get("date");
            const dayFund = getDayFund(dayIndex, storage);
            const attendanceDay = this.entity.Days?.find(d => isSameDay(day, getUtcDayjs(d.Date)));
            map[dayIndex] = attendanceDay ? getDaySaldo(attendanceDay, dayFund, storage) : 0;
            return map;
        }, {});

        const workedHoursMap = monthDays.reduce((map: Record<number, number>, day) => {
            const dayIndex = day.get("date");
            const attendanceDay = this.entity.Days?.find(d => isSameDay(day, getUtcDayjs(d.Date)));
            map[dayIndex] = getDayIntervalsMinutes(attendanceDay, IntervalType.Work);
            return map;
        }, {});

        const actions = parseDayActionsFromData(this.entity.Days);

        storage.setCustomData({
            lastTimeOvertimeWork,
            saldoMap,
            workedHoursMap,
            actions,
            selectedDays: null,
            focusedDay: null,
            payrollSettings
        });
        refreshBalance(storage);

        return super.onAfterLoad();
    }

    handleChange = (e: ISmartFieldChange): void => {
        if (e.bindingContext.getPath() === viewSwitchPath) {
            // hack to prevent setting storage dirty on view switch - we don't want to trigger unsaved
            // changes prompt when switching between views, maybe we can use customData for this in the future
            this.props.storage.setValueByPath(viewSwitchPath, e.value);
        } else {
            this.props.storage.handleChange(e);
        }
        this.props.storage.refresh();
    };

    handleUnlock = async (): Promise<void> => {
        const data = this.onBeforeSave();
        data.StatusCode = PrAttendanceStatusCode.ToProcess;
        await this.save({ data });
    };

    handleLock = async (): Promise<void> => {
        const data = this.onBeforeSave();
        data.StatusCode = PrAttendanceStatusCode.Locked;
        await this.save({ data });
    };

    handleSave = async (): Promise<void> => {
        await this.save();
    };

    onBeforeSave = (): IPrAttendanceEntity => {
        const storage = this.props.storage;
        let entity = cloneDeep(this.entity);
        for (const day of entity.Days) {
            day.Intervals = transformWorkIntervalsBeforeSave(day.Intervals);
        }

        entity = pairIntervalsWithExistingIds(storage, entity);

        return entity;
    };

    clearAttendance = async () => {
        const { storage } = this.props;
        storage.setBusy(false);
        try {
            await clearAttendance(this.entity.Id);
            await storage.reload();
            this.props.onTableRefreshNeeded?.({
                refreshRowOnly: true
            });
            this.props.storage.displaySaveOkMessage(this.props.storage.t("Common:Validation.Saved"));
        } catch (error) {
            logger.error("Failed to clear attendance", error);
        } finally {
            storage.setBusy(false);
        }
    };

    renderButtons = (): ReactElement => {
        const { storage } = this.props;
        const isCompensationType = this.entity.IsStatutoryCompensation;
        const isEditable = this.entity.Status?.Code === PrAttendanceStatusCode.ToProcess;
        const canLockAttendance = this.canLockAttendance;
        const canBeUnlocked = canLockAttendance && this.entity.Status?.Code === PrAttendanceStatusCode.Locked;
        const isProcessed = this.entity.Status?.Code === PrAttendanceStatusCode.Processed;

        return <ButtonGroup wrap={"wrap"}>
            <Button hotspotId={"formCancel"}
                    isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                    onClick={this.handleFormCancel}
                    isTransparent>{storage.t("Common:General.Cancel")}</Button>
            {isEditable && !isCompensationType && (<>
                <Button onClick={this.clearAttendance}
                        isTransparent>{storage.t("Attendance:Form.Clear")}</Button>
                <Button onClick={this.handleSave}
                        isTransparent={canLockAttendance}>{storage.t("Attendance:Form.Save")}</Button>
            </>)}
            {isEditable && canLockAttendance &&
                <Button onClick={this.handleLock}>{storage.t(isCompensationType ? "Attendance:Form.Lock" : "Attendance:Form.SaveAndLock")}</Button>
            }
            {canBeUnlocked && <Button onClick={this.handleUnlock}
                                    isDisabled={isProcessed}>{storage.t("Attendance:Form.Unlock")}</Button>}
        </ButtonGroup>;
    };
}

export default withPermissionContext(AttendanceFormView);